import {
  email,
  max,
  maxLength,
  min,
  minLength,
  pattern,
  required,
  validate,
  type FieldTree,
  type SchemaPath,
  type SchemaPathTree,
} from '@angular/forms/signals';
import { SignalFormValidators } from '@vallift/ngl';
import type { GoogleAddressValue } from '@shared/components/google-address-auto-complete/google-address-auto-complete';
import type { CurrencyFieldValue } from '@shared/components/currency-input/currency.catalog';
import { CURRENCY_DISPLAY_FALLBACK } from '@shared/components/currency-input/currency.utils';
import type { MultiChoiceGridOptions } from '@shared/components/multi-choice-grid/multi-choice-grid';
import type { SingleChoiceGridOptions } from '@shared/components/single-choice-grid/single-choice-grid';
import {
  readNumberGridColumnId,
  type NumberGridValue,
} from '@shared/components/number-grid/number-grid';
import {
  readMultiChoiceDefaults,
  readMultiChoiceGridConfig,
  readNumberGridConfig,
  readSingleChoiceGridConfig,
  type BuiltForm,
  type CurrencyConfig,
  type FieldType,
  type FormField,
} from '../models/built-form';
import { sortedPages } from './form-pages';
import type { CollectionSelectOption } from './resolve-collection-dropdown-items';

const LAYOUT_FIELD_TYPES = new Set<FieldType>([
  'heading',
  'paragraph',
  'divider',
  'spacer',
]);

export type DateRangeAnswer = {
  startDate: Date;
  endDate: Date;
} | null;

export type RangeSliderAnswer = {
  start: number;
  end: number;
};

/** Respondent answer for a collection-backed select. */
export type CollectionSelectAnswer = CollectionSelectOption;

/** Persisted file answer after upload (server draft / submit). */
export type FileIdsAnswer = { fileIds: string[] };

export type AnswerValue =
  | string
  | number
  | boolean
  | null
  | string[]
  | Date
  | DateRangeAnswer
  | RangeSliderAnswer
  | GoogleAddressValue
  | CurrencyFieldValue
  | File[]
  | FileIdsAnswer
  | CollectionSelectAnswer
  | CollectionSelectAnswer[]
  | MultiChoiceGridOptions[]
  | NumberGridValue[]
  | SingleChoiceGridOptions[];

export type RespondentAnswers = Record<string, AnswerValue>;

export function isAnswerField(field: FormField): boolean {
  return !LAYOUT_FIELD_TYPES.has(field.type);
}

export function answerFields(form: BuiltForm): FormField[] {
  return sortedPages(form.pages).flatMap((page) =>
    page.fields.filter(isAnswerField),
  );
}

export function formStructureKey(form: BuiltForm): string {
  return sortedPages(form.pages)
    .map((page) => {
      const fieldKey = page.fields
        .map(
          (field) =>
            `${field.id}:${field.type}:${field.required ? 1 : 0}:${field.disabled ? 1 : 0}:${JSON.stringify(field.validation ?? {})}:${JSON.stringify(field.default ?? null)}:${JSON.stringify(field.config ?? null)}`,
        )
        .join(',');
      return `${page.id}[${fieldKey}]`;
    })
    .join('|');
}

function readCurrencyConfig(field: FormField): CurrencyConfig {
  const config = field.config;
  if (!config) {
    return {};
  }
  return {
    defaultCurrency:
      'defaultCurrency' in config && typeof config.defaultCurrency === 'string'
        ? config.defaultCurrency
        : 'defaultCurrency' in config && config.defaultCurrency === null
          ? null
          : undefined,
    defaultValue:
      'defaultValue' in config && typeof config.defaultValue === 'number'
        ? config.defaultValue
        : undefined,
    allowChangeCurrency:
      'allowChangeCurrency' in config && typeof config.allowChangeCurrency === 'boolean'
        ? config.allowChangeCurrency
        : undefined,
  };
}

function boundCurrencyValue(field: FormField): CurrencyFieldValue {
  const config = readCurrencyConfig(field);
  return {
    currencyType: config.defaultCurrency?.trim() || CURRENCY_DISPLAY_FALLBACK,
    amount: boundNumber(field),
  };
}

function boundString(field: FormField): string {
  const bound = field.default;
  if (bound?.source === 'variable' || bound?.source === 'system') {
    if (typeof bound.value === 'string') {
      return bound.value;
    }
    if (typeof bound.value === 'number' || typeof bound.value === 'boolean') {
      return String(bound.value);
    }
    return '';
  }
  if (bound?.source === 'static' && typeof bound.value === 'string') {
    return bound.value;
  }
  const value = field.config?.defaultValue;
  return typeof value === 'string' ? value : '';
}

function boundNumber(field: FormField): number | null {
  const bound = field.default;
  if (bound?.source === 'variable' || bound?.source === 'system') {
    if (typeof bound.value === 'number') {
      return bound.value;
    }
    if (typeof bound.value === 'string' && bound.value.trim() !== '') {
      const parsed = Number(bound.value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }
  if (bound?.source === 'static' && typeof bound.value === 'number') {
    return bound.value;
  }
  const value = field.config?.defaultValue;
  return typeof value === 'number' ? value : null;
}

function boundBoolean(field: FormField): boolean {
  const value = field.config?.defaultValue;
  return typeof value === 'boolean' ? value : false;
}

function defaultAnswerValue(field: FormField): AnswerValue {
  switch (field.type) {
    case 'checkbox':
    case 'switch':
      return boundBoolean(field);

    case 'checkbox_group':
      return [];

    case 'multi_choice_grid': {
      return readMultiChoiceGridConfig(field).items.map(
        (display): MultiChoiceGridOptions => ({
          display,
          selectedChoiceIds: [],
        }),
      );
    }

    case 'single_choice_grid': {
      return readSingleChoiceGridConfig(field).items.map(
        (display): SingleChoiceGridOptions => ({
          display,
          selectedChoiceId: null,
        }),
      );
    }

    case 'number_grid': {
      const config = readNumberGridConfig(field);
      const columns = (config.columns ?? [])
        .map((column) => ({
          id: readNumberGridColumnId(column),
          label: column.label,
        }))
        .filter((column) => column.id);

      const items = (config.items ?? []).map((item) => {
        if (typeof item === 'string') {
          return { display: item };
        }
        return {
          ...(item.id ? { id: item.id } : {}),
          ...(item.uniqueKey ? { uniqueKey: item.uniqueKey } : {}),
          display: item.display ?? '',
        };
      });

      return items.map(
        (item): NumberGridValue => ({
          ...item,
          values: columns.map((column) => ({
            id: column.id,
            value: null,
          })),
        }),
      );
    }

    case 'multi_choice': {
      const keys = readMultiChoiceDefaults(field);
      if (field.dataLibraryCollection?.dataLibraryId) {
        return keys.map((id) => ({ id, uniqueKey: '', display: '' }));
      }
      return [...keys];
    }

    case 'number':
      return boundNumber(field);

    case 'currency':
      return boundCurrencyValue(field);

    case 'slider': {
      const value = field.config?.defaultValue;
      return typeof value === 'number' ? value : 50;
    }

    case 'rating':
      return null;

    case 'range_slider': {
      const value = field.config?.defaultValue;

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const range = value as { start: number; end: number };

        return {
          start: range.start,
          end: range.end,
        };
      }

      return {
        start: field.sliderMin ?? 0,
        end: field.sliderMax ?? 100,
      };
    }

    case 'date':
      return field.dateDefault ?? null;

    case 'date_range': {
      const startDate = field.startDate;
      const endDate = field.endDate;

      if (!startDate || !endDate) {
        return null;
      }

      return { startDate, endDate };
    }

    case 'image_picker':
    case 'camera':
    case 'file_uploader':
      return [];


    case 'map':
      return field.mapDefaultLocation ?? null;

    case 'address_selector':
      return null;

    case 'select':
      if (field.dataLibraryCollection?.dataLibraryId) {
        const id =
          typeof field.config?.defaultValue === 'string'
            ? field.config.defaultValue.trim()
            : field.default?.source === 'static' &&
                typeof field.default.value === 'string'
              ? field.default.value.trim()
              : '';
        return id ? { id, uniqueKey: '', display: '' } : null;
      }
      return boundString(field);
    case 'searchable_picker': {
      const id =
        typeof field.config?.defaultValue === 'string'
          ? field.config.defaultValue.trim()
          : field.default?.source === 'static' &&
              typeof field.default.value === 'string'
            ? field.default.value.trim()
            : '';
      return id ? { id, uniqueKey: '', display: '' } : null;
    }
    case 'single_choice':
      if (field.dataLibraryCollection?.dataLibraryId) {
        const id =
          typeof field.config?.defaultValue === 'string'
            ? field.config.defaultValue.trim()
            : field.default?.source === 'static' &&
                typeof field.default.value === 'string'
              ? field.default.value.trim()
              : '';
        return id ? { id, uniqueKey: '', display: '' } : null;
      }
      return boundString(field);
    case 'text':
    case 'textarea':
    case 'email':
    case 'phone':
    case 'url':
    case 'radio_group':
      return boundString(field);

    default:
      return boundString(field);
  }
}

export function createAnswerDefaults(form: BuiltForm): RespondentAnswers {
  const answers: RespondentAnswers = {};
  for (const field of answerFields(form)) {
    answers[field.id] = defaultAnswerValue(field);
  }
  return answers;
}

function isFieldRequired(field: FormField): boolean {
  return field.required === true || field.validation?.required === true;
}

function requiredMessage(field: FormField): string {
  return field.validation?.customMessage?.trim() || 'This field is required';
}

function applyFieldRules(path: SchemaPath<AnswerValue>, field: FormField): void {
  const validation = field.validation;
  const message = requiredMessage(field);

  if (field.type === 'checkbox' && isFieldRequired(field)) {
    validate(path as SchemaPath<boolean>, ({ value }) => {
      if (value() === true) {
        return undefined;
      }
      return { kind: 'required', message };
    });
    return;
  }

  if (
    (field.type === 'checkbox_group' || field.type === 'multi_choice') &&
    isFieldRequired(field)
  ) {
    validate(path as SchemaPath<string[]>, ({ value }) => {
      const current = value();
      if (Array.isArray(current) && current.length > 0) {
        return undefined;
      }
      return { kind: 'required', message };
    });
    return;
  }

  if (
    (field.type === 'image_picker' ||
      field.type === 'camera' ||
      field.type === 'file_uploader') &&
    isFieldRequired(field)
  ) {
    validate(path as SchemaPath<File[] | FileIdsAnswer>, ({ value }) => {
      const current = value();
      if (Array.isArray(current) && current.length > 0) {
        return undefined;
      }
      if (
        current != null &&
        typeof current === 'object' &&
        !Array.isArray(current) &&
        'fileIds' in current &&
        Array.isArray((current as FileIdsAnswer).fileIds) &&
        (current as FileIdsAnswer).fileIds.length > 0
      ) {
        return undefined;
      }
      return { kind: 'required', message };
    });
    return;
  }

  if (field.type === 'address_selector' && isFieldRequired(field)) {
    validate(path as SchemaPath<GoogleAddressValue | null>, ({ value }) => {
      const current = value();
      if (current != null && current.address.trim() !== '') {
        return undefined;
      }
      return { kind: 'required', message };
    });
    return;
  }

  if (
    (field.type === 'select' ||
      field.type === 'searchable_picker' ||
      field.type === 'single_choice') &&
    field.dataLibraryCollection?.dataLibraryId &&
    isFieldRequired(field)
  ) {
    validate(path as SchemaPath<CollectionSelectAnswer | null>, ({ value }) => {
      const current = value();
      if (current != null && typeof current.id === 'string' && current.id.trim()) {
        return undefined;
      }
      return { kind: 'required', message };
    });
    return;
  }

  if (field.type === 'currency') {
    const currencyPath = path as SchemaPath<CurrencyFieldValue | null>;
    if (isFieldRequired(field)) {
      // Marks the field REQUIRED so [formField] shows the required indicator.
      required(currencyPath, { message });
      validate(currencyPath, ({ value }) => {
        const current = value();
        // Object values are never "empty" for required(); enforce amount presence.
        if (current != null && typeof current.amount === 'number') {
          return undefined;
        }
        return { kind: 'required', message };
      });
    }
    if (validation?.min != null) {
      const minAmount = validation.min;
      validate(currencyPath, ({ value }) => {
        const amount = value()?.amount;
        if (amount == null || amount >= minAmount) {
          return undefined;
        }
        return {
          kind: 'min',
          message:
            validation.customMessage?.trim() ||
            `Enter an amount of at least ${minAmount}`,
        };
      });
    }
    if (validation?.max != null) {
      const maxAmount = validation.max;
      validate(currencyPath, ({ value }) => {
        const amount = value()?.amount;
        if (amount == null || amount <= maxAmount) {
          return undefined;
        }
        return {
          kind: 'max',
          message:
            validation.customMessage?.trim() ||
            `Enter an amount no greater than ${maxAmount}`,
        };
      });
    }
    return;
  }

  if (isFieldRequired(field)) {
    required(path, { message });
  }

  if (
    field.type === 'text' ||
    field.type === 'textarea' ||
    field.type === 'email' ||
    field.type === 'phone' ||
    field.type === 'url' ||
    (field.type === 'select' && !field.dataLibraryCollection?.dataLibraryId) ||
    field.type === 'radio_group'
  ) {
    const stringPath = path as SchemaPath<string>;
    if (validation?.minLength != null) {
      minLength(stringPath, validation.minLength, {
        message:
          validation.customMessage?.trim() ||
          `Minimum ${validation.minLength} characters required`,
      });
    }
    if (validation?.maxLength != null) {
      maxLength(stringPath, validation.maxLength, {
        message:
          validation.customMessage?.trim() ||
          `Maximum ${validation.maxLength} characters allowed`,
      });
    }
    if (validation?.pattern?.trim()) {
      try {
        pattern(stringPath, new RegExp(validation.pattern), {
          message: validation.customMessage?.trim() || 'Invalid format',
        });
      } catch {
        // Ignore invalid authoring patterns.
      }
    }
  }

  if (field.type === 'email') {
    email(path as SchemaPath<string>, {
      message: validation?.customMessage?.trim() || 'Enter a valid email',
    });
  }

  if (field.type === 'phone') {
    SignalFormValidators.phoneNumber(path as SchemaPath<string>, {
      message: validation?.customMessage?.trim() || 'Enter a valid phone number',
    });
  }

  if (field.type === 'number') {
    const numberPath = path as SchemaPath<number | null>;
    if (validation?.min != null) {
      min(numberPath, validation.min, {
        message:
          validation.customMessage?.trim() ||
          `Minimum ${validation.min} required`,
      });
    }
    if (validation?.max != null) {
      max(numberPath, validation.max, {
        message:
          validation.customMessage?.trim() ||
          `Maximum ${validation.max} allowed`,
      });
    }
    if (validation?.integerOnly) {
      SignalFormValidators.integerOnly(numberPath, {
        message: validation.customMessage?.trim() || 'Must be a whole number',
      });
    }
  }

  if (field.type === 'date') {
    const datePath = path as SchemaPath<Date | null>;
    if (field.dateDisablePast) {
      SignalFormValidators.onlyFutureDate(datePath, {
        message:
          validation?.customMessage?.trim() || 'Date must be in the future',
      });
    }
    if (field.dateDisableFuture) {
      SignalFormValidators.onlyPastDate(datePath, {
        message: validation?.customMessage?.trim() || 'Date must be in the past',
      });
    }
  }

  if (field.type === 'date_range') {
    const rangePath = path as SchemaPath<DateRangeAnswer>;
    SignalFormValidators.validateDateRange(rangePath, {
      message:
        validation?.customMessage?.trim() ||
        'End date must be on or after start date',
    });
    if (field.dateRangeAllowOnlyPast) {
      validate(rangePath, ({ value }) => {
        const range = value();
        if (!range?.endDate) {
          return undefined;
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const end = new Date(range.endDate);
        end.setHours(0, 0, 0, 0);
        return end.getTime() < today.getTime()
          ? undefined
          : {
              kind: 'onlyPastDate',
              message:
                validation?.customMessage?.trim() ||
                'Date range must be in the past',
            };
      });
    }
    if (field.dateRangeAllowOnlyFuture) {
      validate(rangePath, ({ value }) => {
        const range = value();
        if (!range?.startDate) {
          return undefined;
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const start = new Date(range.startDate);
        start.setHours(0, 0, 0, 0);
        return start.getTime() > today.getTime()
          ? undefined
          : {
              kind: 'onlyFutureDate',
              message:
                validation?.customMessage?.trim() ||
                'Date range must be in the future',
            };
      });
    }
  }
}

export function applyAnswerValidators(
  schema: SchemaPathTree<RespondentAnswers>,
  form: BuiltForm,
): void {
  for (const field of answerFields(form)) {
    const path = (schema as unknown as Record<string, SchemaPath<AnswerValue>>)[
      field.id
    ];
    if (!path) {
      continue;
    }
    applyFieldRules(path, field);
  }
}

export function validateAnswerFieldIds(
  answersForm: FieldTree<RespondentAnswers>,
  fieldIds: string[],
): boolean {
  let valid = true;
  let firstInvalid: FieldTree<AnswerValue> | null = null;

  for (const id of fieldIds) {
    const control = (
      answersForm as unknown as Record<string, FieldTree<AnswerValue>>
    )[id];
    if (!control) {
      continue;
    }
    control().markAsTouched();
    if (control().invalid()) {
      valid = false;
      if (!firstInvalid) {
        firstInvalid = control;
      }
    }
  }

  firstInvalid?.().focusBoundControl();
  return valid;
}

export function answerFieldTree(
  answersForm: FieldTree<RespondentAnswers>,
  fieldId: string,
): FieldTree<AnswerValue> | undefined {
  return (answersForm as unknown as Record<string, FieldTree<AnswerValue>>)[
    fieldId
  ];
}

/** JSON-safe snapshot for display / console (Dates → ISO, Files → name). */
export function serializeAnswersForDisplay(
  answers: RespondentAnswers,
): Record<string, unknown> {
  return JSON.parse(
    JSON.stringify(answers, (_key, value: unknown) => {
      if (value instanceof Date) {
        return value.toISOString();
      }
      if (typeof File !== 'undefined' && value instanceof File) {
        return { name: value.name, size: value.size, type: value.type };
      }
      return value;
    }),
  ) as Record<string, unknown>;
}

export function formatAnswersJson(answers: RespondentAnswers): string {
  return JSON.stringify(serializeAnswersForDisplay(answers), null, 2);
}
